import type {
    ConversionAlgoConfig,
    ConversionDal,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    StructureIterators,
    VirtualStructureEntryItem
} from '../../types'
import {createServerRequest} from './request'
import {CTX_SERVER, VIRTUAL_TAG} from './constants'
import {LayoutIntentType, type Rect} from '@wix/document-services-types'
import type {GroupingResult} from './types'
import {getMeasurementsPointer, getStructurePointer} from '../../conversionDal/utils/pointer'
import type {StructureEntryItem} from '../../conversionDal/types'
import {VirtualStructureTag} from '../../enums'
import type {SnapshotDal} from '@wix/document-manager-core'
import {getReportableFromError} from '@wix/document-manager-utils'

export const groupingAIResult = async (
    structureIterators: StructureIterators,
    conversionDal: ConversionDal,
    pageId: string,
    pluginInitArgs: MobileAlgoPluginInitializationArgs,
    contextHelper: PluginContext,
    config: ConversionAlgoConfig,
    initialSnapshot: SnapshotDal
): Promise<GroupingResult> => {
    const {readOnlyExtensionAPI, environmentContext} = pluginInitArgs
    const requestData = createServerRequest(
        structureIterators,
        conversionDal,
        pageId,
        readOnlyExtensionAPI,
        config,
        contextHelper,
        initialSnapshot
    )
    const sendRequest = contextHelper.get(CTX_SERVER)

    try {
        return await sendRequest(readOnlyExtensionAPI, environmentContext, requestData)
    } catch (e) {
        throw getReportableFromError(e, {
            errorType: 'GroupingModelNetworkError',
            extras: {
                requestData
            }
        })
    }
}

const calculateRealChildren = (children: VirtualStructureEntryItem[]): string[] =>
    children.flatMap(child => (child.tag === VirtualStructureTag.Virtual ? child.realChildren : [child.id]))

const calculateMeasurements = (children: VirtualStructureEntryItem[]): Rect => {
    const groupMeasurements = {left: Infinity, top: Infinity, right: 0, bottom: 0}

    children.forEach(({measurements}) => {
        groupMeasurements.left = Math.min(groupMeasurements.left, measurements.x)
        groupMeasurements.top = Math.min(groupMeasurements.top, measurements.y)
        groupMeasurements.right = Math.max(groupMeasurements.right, measurements.x + measurements.width)
        groupMeasurements.bottom = Math.max(groupMeasurements.bottom, measurements.y + measurements.height)
    })

    return {
        x: groupMeasurements.left,
        y: groupMeasurements.top,
        width: groupMeasurements.right - groupMeasurements.left,
        height: groupMeasurements.bottom - groupMeasurements.top
    }
}

const getMeasurements = (conversionDal: ConversionDal, group: GroupingResult) => {
    return conversionDal.get(getMeasurementsPointer({id: group.id}))
}

export const createVirtualGroupMap = (
    serverResponse: GroupingResult,
    conversionDal: ConversionDal
): Record<string, VirtualStructureEntryItem> => {
    const componentToChildrenGroupMap: Record<string, VirtualStructureEntryItem> = {}

    const scanGroupingResultAndBuildGroupsTree = (entry: GroupingResult, parent: GroupingResult) => {
        if (entry.tag !== 'Group') {
            const compStructure = conversionDal.get(getStructurePointer({id: entry.id}))
            componentToChildrenGroupMap[entry.id] = {
                id: entry.id,
                components: [],
                componentType: compStructure.componentType,
                tag: VirtualStructureTag.Real,
                measurements: getMeasurements(conversionDal, entry),
                parentId: parent.id,
                realChildren: []
            }

            return
        }

        const isSelfGroup = entry.children!.some(child => child.id === entry.id)
        const filteredChildren = entry.children!.filter(child => child.id !== entry.id)
        const filteredChildrenIds = filteredChildren.map(child => child.id)

        for (const child of filteredChildren) {
            scanGroupingResultAndBuildGroupsTree(child, entry)
        }

        const resolvedChildren = filteredChildrenIds.map(childId => componentToChildrenGroupMap[childId])
        const realChildren = calculateRealChildren(resolvedChildren)

        const intermediateStructure = {
            id: entry.id,
            components: filteredChildrenIds,
            realChildren,
            parentId: parent.id
        }

        if (isSelfGroup) {
            const compStructure = conversionDal.get(getStructurePointer({id: entry.id})) as StructureEntryItem
            const components =
                compStructure?.layoutIntent?.type === LayoutIntentType.SuperGrid ? realChildren : filteredChildrenIds
            Object.assign(intermediateStructure, {
                components,
                componentType: compStructure.componentType,
                tag: VirtualStructureTag.Real,
                measurements: getMeasurements(conversionDal, entry)
            })
        } else {
            Object.assign(intermediateStructure, {
                componentType: VIRTUAL_TAG,
                tag: VirtualStructureTag.Virtual,
                measurements: calculateMeasurements(resolvedChildren)
            })
        }

        componentToChildrenGroupMap[entry.id] = intermediateStructure as VirtualStructureEntryItem

        for (const childId of componentToChildrenGroupMap[entry.id].components) {
            componentToChildrenGroupMap[childId].parentId = entry.id
        }

        const unreferencedChildren = filteredChildrenIds.filter(
            childId => !componentToChildrenGroupMap[entry.id].components.includes(childId)
        )

        for (const childId of unreferencedChildren) {
            delete componentToChildrenGroupMap[childId]
        }
    }

    scanGroupingResultAndBuildGroupsTree(serverResponse.children![0], serverResponse)

    return componentToChildrenGroupMap
}
